/** Date helpers for UI text. Times are shown in the account's timezone when known. */

export function formatDateTime(iso: string, locale: string, timeZone?: string): string {
  const date = new Date(iso);
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timeZone && { timeZone }),
    }).format(date);
  } catch {
    // Unknown timezone/locale id: fall back to the browser defaults.
    return date.toLocaleString();
  }
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Whole days and hours left until `iso` (both 0 when it is less than an hour away or past). */
export function timeLeft(iso: string, now: number = Date.now()): { days: number; hours: number } {
  const ms = Math.max(0, new Date(iso).getTime() - now);
  return { days: Math.floor(ms / DAY), hours: Math.floor((ms % DAY) / HOUR) };
}

/** `10 окт` / `Oct 10` for a calendar date (`YYYY-MM-DD`): no time-zone conversion applies. */
export function formatCalendarDate(date: string, locale: string, withYear = false): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/** `18:30` from the server's `HH:MM[:SS]`. */
export function formatClock(time: string): string {
  return time.slice(0, 5);
}

/** Compact deadline text: `10 окт` or `10 окт · 18:30`. */
export function formatDeadline(
  deadline: { date: string; time: string | null },
  locale: string,
): string {
  const date = formatCalendarDate(deadline.date, locale);
  return deadline.time ? `${date} · ${formatClock(deadline.time)}` : date;
}

interface PeriodLike {
  date: string;
  time: string | null;
  start_date: string | null;
  start_time: string | null;
}

/** `10 окт · 18:30`, or `5 окт → 10 окт · 18:30` for a period with a start. */
export function formatPeriod(deadline: PeriodLike, locale: string): string {
  const end = formatDeadline(deadline, locale);
  if (!deadline.start_date) return end;
  const start = formatDeadline({ date: deadline.start_date, time: deadline.start_time }, locale);
  return `${start} → ${end}`;
}

/** `5 – 11 октября` / `28 сент. – 4 окт.` for an inclusive ISO week (no time zone applies). */
export function formatWeekRange(first: string, last: string, locale: string): string {
  const utc = (value: string) => {
    const [year = 1970, month = 1, day = 1] = value.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };
  const a = utc(first);
  const b = utc(last);
  const sameMonth =
    a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: sameMonth ? 'long' : 'short',
    timeZone: 'UTC',
  }).formatRange(a, b);
}

/** Visual truncation to `max` characters with an ellipsis; `text` is returned when it fits. */
export function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** Wall-clock fields of an instant in ``timeZone`` read back as if they were UTC. */
function wallAsUtc(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
}

/**
 * The instant of a wall-clock date (``YYYY-MM-DD``) and time (``HH:MM``) in ``timeZone``.
 * A time skipped by a DST jump does not exist and gives ``null``; a repeated one (the clock
 * going back) resolves to its FIRST occurrence.
 */
export function zonedToInstant(date: string, time: string, timeZone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const clock = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match || !clock) return null;
  const wall = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(clock[1]),
    Number(clock[2]),
  );
  let guess = wall - (wallAsUtc(wall, timeZone) - wall);
  guess = wall - (wallAsUtc(guess, timeZone) - guess);
  if (wallAsUtc(guess, timeZone) !== wall) return null; // inside a DST gap
  // Repeated hour: an earlier instant with the same wall time may exist (the first occurrence).
  const earlier = guess - 3_600_000;
  if (wallAsUtc(earlier, timeZone) === wall) guess = earlier;
  return new Date(guess);
}
